import REGEN_PRESETS_DATA from './regenBrakingPresets.json';
import STRATEGIES_DATA from './batteryThermalStrategies.json';

// Battery Pack Thermal Simulator: field metadata + client-side validation. Keys mirror
// backend/simulation/schemas.py BatteryThermalRequest exactly; ranges match the Pydantic
// Fields. Cooling-strategy presets come from batteryThermalStrategies.json, which is generated
// from (and test-checked against) backend/simulation/battery_thermal.py.
export { validateField, toRawStrings, CHEMISTRY_OPTIONS } from './simulationTools';

export const PROFILE_OPTIONS = [
  { value: 'drive', label: 'Drive cycle' },
  { value: 'charge', label: 'DC fast charge' },
  { value: 'drive_then_charge', label: 'Drive, then DC fast charge' },
];
export const isDriveProfile = (p) => p === 'drive' || p === 'drive_then_charge';
export const isChargeProfile = (p) => p === 'charge' || p === 'drive_then_charge';

export const DRIVE_CYCLE_OPTIONS = [
  { value: 'wltc', label: 'WLTC (30 min)' },
  { value: 'midc', label: 'MIDC (20 min)' },
  { value: 'us06', label: 'US06 aggressive (10 min)' },
];
// Repeats cap = about 3.3 h of driving (backend BATTERY_THERMAL_MAX_DRIVE_S = 12,000 s).
export const MAX_REPEATS = { wltc: 6, midc: 10, us06: 20 };

export const PACK_FIELD_SPECS = [
  { key: 'pack_energy_kwh', label: 'Pack Energy', unit: 'kWh', min: 5, max: 300, strictlyPositive: true },
  { key: 'pack_nominal_voltage_v', label: 'Pack Nominal Voltage', unit: 'V', min: 48, max: 1000, strictlyPositive: true },
  { key: 'start_soc_pct', label: 'Start SoC', unit: '%', min: 1, max: 100, strictlyPositive: true },
];
export const AMBIENT_SPEC = { key: 'ambient_temp_c', label: 'Ambient Temperature', unit: '°C', min: -30, max: 50 };
export const START_TEMP_SPEC = { key: 'start_temp_c', label: 'Start Pack Temperature', unit: '°C', min: -30, max: 60 };
export const REPEATS_SPEC = (cycle) => ({
  key: 'repeats', label: 'Cycle Repeats', unit: '×', min: 1, max: MAX_REPEATS[cycle], strictlyPositive: true, integer: true,
});
export const CHARGE_FIELD_SPECS = [
  { key: 'charger_power_kw', label: 'Charger Power', unit: 'kW', min: 10, max: 400, strictlyPositive: true },
  { key: 'vehicle_max_charge_kw', label: 'Vehicle Max DC Power', unit: 'kW', min: 5, max: 1000, strictlyPositive: true },
  { key: 'target_soc_pct', label: 'Target SoC', unit: '%', min: 5, max: 100, strictlyPositive: true },
];
export const COOLING_FIELD_SPECS = [
  { key: 'pack_to_coolant_ua_w_per_k', label: 'Pack-to-Coolant UA', unit: 'W/K', min: 1, max: 20000, strictlyPositive: true },
  { key: 'coolant_thermal_mass_kj_per_k', label: 'Coolant Thermal Mass', unit: 'kJ/K', min: 0.5, max: 2000, strictlyPositive: true },
  { key: 'coolant_to_ambient_ua_w_per_k', label: 'Coolant-to-Ambient UA', unit: 'W/K', min: 0, max: 5000 },
  { key: 'radiator_ua_w_per_k', label: 'Radiator UA (cooling on)', unit: 'W/K', min: 0, max: 5000 },
  { key: 'cooling_power_kw', label: 'Chiller Electrical Power', unit: 'kW', min: 0, max: 50 },
  { key: 'cooling_cop', label: 'Chiller COP', unit: '×', min: 0.5, max: 8, strictlyPositive: true },
  { key: 'heating_power_kw', label: 'Heater Electrical Power', unit: 'kW', min: 0, max: 50 },
  { key: 'heating_cop', label: 'Heater COP', unit: '×', min: 0.2, max: 3, strictlyPositive: true },
];

export const STRATEGIES = STRATEGIES_DATA;
export const STRATEGY_OPTIONS = STRATEGIES.map((s) => ({ value: s.value, label: s.label }));
export const STRATEGY_LABELS = Object.fromEntries(STRATEGIES.map((s) => [s.value, s.label]));
const REFERENCE_STRATEGY_KWH = 60;
// Conductances and thermal masses scale with pack energy (cell area and coolant volume both
// scale with cell count); hardware ratings (chiller, heater, radiator) and COPs do not —
// same rule as backend strategy_params_for_pack().
const SCALED_KEYS = ['pack_to_coolant_ua_w_per_k', 'coolant_thermal_mass_kj_per_k', 'coolant_to_ambient_ua_w_per_k'];
// Round for display without changing preset values: whole numbers from 100 up, else 2 decimals
// (COPs like 0.95 must survive).
const displayRound = (v) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100);

export function strategyParamsForPack(strategy, packKwh) {
  const s = STRATEGIES.find((x) => x.value === strategy) || STRATEGIES[0];
  const k = Number(packKwh) > 0 ? Number(packKwh) / REFERENCE_STRATEGY_KWH : 1;
  return Object.fromEntries(Object.entries(s.per60kWh).map(([key, v]) => {
    const scaled = SCALED_KEYS.includes(key) ? v * k : v;
    return [key, String(displayRound(scaled))];
  }));
}

// Vehicle presets: the Regen Braking Simulator's vehicles (road load, motor, pack), plus the
// cooling architecture and max DC charge power each car actually has:
//   Nexon EV Max  liquid loop with chiller, 50 kW DC (Tata spec)
//   Model 3 RWD   liquid loop with chiller, 170 kW DC (Tesla spec, LFP)
//   Leaf 40 kWh   no active battery cooling (passive), 50 kW CHAdeMO (Nissan spec)
//   Bolt EV/Ampera-e  liquid loop with chiller, 55 kW DC (GM spec)
const PRESET_THERMAL = {
  'tata-nexon-ev-max': { strategy: 'liquid_chiller', vehicleMaxChargeKw: 50 },
  'tesla-model-3-rwd-lfp': { strategy: 'liquid_chiller', vehicleMaxChargeKw: 170 },
  'nissan-leaf-40': { strategy: 'passive_air', vehicleMaxChargeKw: 50 },
  'chevrolet-bolt-ampera-e': { strategy: 'liquid_chiller', vehicleMaxChargeKw: 55 },
};
export const THERMAL_PRESETS = REGEN_PRESETS_DATA.filter((p) => PRESET_THERMAL[p.id]).map((p) => ({
  id: p.id,
  label: p.label,
  vehicle: {
    mass_kg: p.vehicle.mass_kg, payload_kg: p.vehicle.payload_kg, cd: p.vehicle.cd,
    frontal_area_m2: p.vehicle.frontal_area_m2, crr: p.vehicle.crr, wheel_radius_m: p.vehicle.wheel_radius_m,
    gear_ratio: p.vehicle.gear_ratio, driveline_efficiency: p.vehicle.driveline_efficiency,
    peak_torque_nm: p.motor.peak_torque_nm, peak_power_kw: p.motor.peak_power_kw,
    drive_technology: p.motor.drive_technology,
  },
  chemistry: p.battery.chemistry,
  pack_energy_kwh: p.battery.pack_energy_kwh,
  pack_nominal_voltage_v: p.battery.pack_nominal_voltage_v,
  aux_power_w: p.battery.aux_power_w,
  min_soc_pct: Math.max(p.battery.min_soc_pct, 5),
  strategy: PRESET_THERMAL[p.id].strategy,
  vehicle_max_charge_kw: PRESET_THERMAL[p.id].vehicleMaxChargeKw,
}));
export const DEFAULT_PRESET_ID = 'tesla-model-3-rwd-lfp';
export const findPreset = (id) => THERMAL_PRESETS.find((p) => p.id === id) || null;

export const DEFAULT_CONDITIONS = { ambient_temp_c: '35', start_temp_c: '35' };
export const DEFAULT_PROFILE = {
  profile: 'drive_then_charge', drive_cycle: 'us06', repeats: '4',
  charger_power_kw: '150', target_soc_pct: '80',
};
export const DEFAULT_START_SOC = '90';

// Mirrors BatteryThermalRequest.check_profile; returns { fieldKey: message }.
export function validateCrossFields(profile, values) {
  const errors = {};
  if (profile === 'charge' && values.target_soc_pct !== undefined && values.start_soc_pct !== undefined
      && values.target_soc_pct <= values.start_soc_pct) {
    errors.target_soc_pct = 'Must be above Start SoC.';
  }
  if (isDriveProfile(profile) && values.start_soc_pct !== undefined && values.min_soc_pct !== undefined
      && values.start_soc_pct <= values.min_soc_pct) {
    errors.start_soc_pct = `Must be above the ${values.min_soc_pct}% minimum SoC.`;
  }
  return errors;
}
